import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import {
    Checkbox,
    Label,
    LazyRender,
    Panel,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { ActiveDealIndicator,
    InventoryListingAddOns,
    InventoryListingFooter,
    InventoryListingImage,
    InventoryListingOverlayActions,
    InventoryListingTitle,
} from 'reaxl-listing';
import {
    ListingPaymentCTA,
    PricingBadges,
    PricingV2,
} from 'reaxl-molecules';
import { InventoryCarousel } from 'reaxl-organisms';
import { WalletPaymentDetailsV2 } from 'reaxl-wallet';

import { getAlphaClickType } from '@/utilities/getAlphaTaggingType';

import { compareAddListingClick } from '@/analytics/inventoryAnalyticsHandlers';

import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';

import InventoryDealerInfo from '@/components/InventoryDealerInfo/index.js';

/*
 * InventoryListingAlpha is a specialized alpha variant of the inventory listing component
 * Optimized specifically for alpha uiContext with landscape orientation and brand-secondary background
 *
 * ALPHA LAYOUT:
 * ,----------------------------
 * |   Addons                   |
 * |............................|
 * |             |  Header      |
 * |   SideBar   |  Body        |
 * |             |..............|
 * |             |  Footer      |
 *  ----------------------------
 */
function InventoryAlphaListing({
    addOns = {},
    carouselSettings,
    emailOwnerProps = {},
    enableMyWallet,
    isActiveDeal = false,
    isAlphaMarket,
    isExactMatch = true,
    isSaved = false,
    lazyLoad = true,
    listing = {},
    listings = [],
    listingCTA = {},
    listingsCount,
    loading,
    onActiveDealDetailsClick = () => { },
    onAlphaListingClick = () => { },
    onAlphaWalletClick = () => { },
    onCheckStatusClick = () => { },
    onCollapsibleHeaderClick = () => { },
    onContextMenu = () => { },
    onInventoryListingClick = () => { },
    onPaymentCTAClick = () => { },
    onPricingDetailClick = () => { },
    onSaveToggle = () => { },
    onSettle = () => { },
    onTileClick = () => { },
    onViewOwnerInventoryClick = () => { },
    orientation,
    owner,
    parentId = '',
    personalPaymentProps,
    phoneClickHandler = () => { },
    savedListingIds,
    shareButton,
    showAlphaPaymentRibbon,
    showCompareButton,
    updateListingPayments,
}) {
    const dispatch = useDispatch();

    const showAlphaCarousel = useSelector(srpActiveInteractionDuck.selectors.getShowAlphaCarousel);
    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);

    const compareListingIds = useSelector(compareListingsDuck.selectors.getActiveListingIds, _isEqual);
    const isCompared = useCallback((listingItem) => compareListingIds?.includes(listingItem?.id), [compareListingIds]);

    const hasIncentive = _get(listing, 'hasSpecialOffer', false)
        && _get(listing, 'listingType', '').toLowerCase() === 'new';

    let carouselListings = listings;

    if (listings && listings.length > 12) {
        carouselListings = listings.slice(0, 12);
    }

    const extraDealerInfoProps = {
        phoneClickHandler,
        orientation,
        onTileClick,
        onCollapsibleHeaderClick,
        lazyLoad,
        listing,
        listingsCount,
        owner,
        uiContext: 'inventory',
        key: 'alpha-listing-extra-dealer-info',
    };

    const dealerInfoProps = {
        emailOwnerProps,
        loading,
        orientation,
        parentId,
        ...extraDealerInfoProps,
    };

    const titleProps = {
        ...listingCTA,
        title: listing.title || listingCTA?.title,
        size: 400,
        onContextMenu,
        uiContext: 'alpha',
    };

    const renderPaymentDetails = (className) => (
        !isActiveDeal && (
            <LazyRender
                key="paymentDetails"
                height={22}
            >
                <div
                    className={clsx(className, 'display-flex')}
                >
                    <WalletPaymentDetailsV2
                        listingId={listing.id}
                        onMyWalletClick={onAlphaWalletClick}
                        personalPaymentProps={personalPaymentProps}
                        uiContext="ribbon"
                        updateListingPayments={updateListingPayments}
                    />
                </div>
            </LazyRender>
        )
    );

    const handleCompareListingsClick = useCallback((compareListing) => async (event) => {
        sendClick(compareAddListingClick, event, {
            clickType: 'alpha',
            inventoryId: compareListing?.id,
        });

        dispatch(userPreferencesDuck.creators.setKeys({ showDrawer: true }));
        if (isCompared(compareListing) && compareListingIds.includes(compareListing.id)) {
            dispatch(compareListingsDuck.creators.removeFromCompare(compareListing?.id));
        } else {
            dispatch(compareListingsDuck.creators.addToCompare(compareListing?.id));
        }
    }, [dispatch, compareListingIds, isCompared]);

    return (
        <Panel
            data-cmp="inventoryAlphaListing"
            className="bg-brand-secondary inventory-listing"
        >
            <Panel.Body className="padding-0 display-flex flex-grow-1 flex-column">
                <div
                    onClick={onAlphaListingClick}
                    role="button"
                    tabIndex="0"
                    aria-label={`Get more details about this ${listing?.title || ''} from ${owner?.name || ''}`}
                >
                    {compareToggle && (
                        <button
                            type="button"
                            className="bg-brand-secondary padding-3 text-left border-0 width-100"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Checkbox
                                name="compareCheckbox"
                                checked={isCompared(listing)}
                                label="Compare"
                                labelClassName="margin-vertical-0"
                                value={isCompared(listing)}
                                onClick={handleCompareListingsClick(listing)}
                            />
                        </button>
                    )}
                    <div className={clsx('display-flex', { 'flex-column': orientation === 'portrait' })}>
                        <div className="alpha-sidebar">
                            <InventoryListingAddOns
                                listing={listing}
                                key="listingAddOns"
                                shareButton={shareButton}
                                {...addOns}
                            />
                            <div className="overlay-actions-container padding-top-2">
                                <InventoryListingOverlayActions
                                    onSaveToggle={onSaveToggle}
                                    listing={listing}
                                    isSaved={isSaved}
                                    show360
                                />
                            </div>
                            <InventoryListingImage
                                data={listing}
                                lazyLoad={lazyLoad}
                            />
                        </div>
                        <div className="display-flex flex-column flex-grow-1 justify-content-between padding-horizontal-3 padding-top-3">
                            <div>
                                <InventoryListingTitle {...titleProps} />
                            </div>

                            <div className="margin-bottom-auto">
                                <div className="pricing-payments">
                                    <PricingV2
                                        key="inventory-listing-pricing"
                                        data={listing.pricingDetail}
                                        uiContext="details"
                                        onPricingDetailClick={onPricingDetailClick}
                                    />
                                    {showAlphaPaymentRibbon && renderPaymentDetails()}
                                    {!showAlphaPaymentRibbon && enableMyWallet && !listing.accelerate && (
                                        <ListingPaymentCTA
                                            key="paymentCTA"
                                            className="text-size-200"
                                            onPaymentCTAClick={onPaymentCTAClick}
                                        />
                                    )}
                                </div>

                                <div
                                    key="alphaBadge"
                                    className="display-flex align-items-start"
                                >
                                    <PricingBadges
                                        dealIndicator={listing.pricingDetail?.dealIndicator}
                                        hasIncentive={hasIncentive}
                                    />
                                    {!isExactMatch && (
                                        <Label
                                            key="alpha-similar-match"
                                            bsStyle="info-inverse"
                                            data-cmp="similarMatchBadge"
                                            aria-label="This listing is a similar match to your search criteria"
                                            className="margin-left-1"
                                        >
                                            Similar Match
                                        </Label>
                                    )}
                                </div>
                            </div>

                            <div className="text-subdued-lightest text-size-100 margin-top-auto">
                                {listing.owner && (
                                    isActiveDeal ? (
                                        <ActiveDealIndicator
                                            orientation={orientation}
                                            onActiveDealDetailsClick={onActiveDealDetailsClick}
                                            onCheckStatusClick={onCheckStatusClick}
                                        />
                                    ) : (
                                        <InventoryListingFooter
                                            className="padding-0"
                                            clickType="alpha"
                                            uiContext="alpha"
                                            orientation={orientation}
                                            listing={listing}
                                            dealsDuck={dealsDuck}
                                            paymentsDuck={paymentsDuck}
                                            interestRateDuck={interestRateDuck}
                                            saveDealXgFetcher={saveDealXgFetcher}
                                            emailOwnerProps={emailOwnerProps}
                                            isPhoneNumberCallable={orientation === 'portrait'}
                                        />
                                    )
                                )}
                                <InventoryDealerInfo {...dealerInfoProps} />
                            </div>

                        </div>
                    </div>
                </div>
            </Panel.Body>
            {showAlphaCarousel
                && (
                    <Panel.Footer className="bg-brand-secondary padding-0 border-top-0">
                        <InventoryCarousel
                            listings={carouselListings}
                            clickType={getAlphaClickType(isAlphaMarket, false)}
                            onInventoryListingClick={onInventoryListingClick}
                            onSaveToggle={onSaveToggle}
                            onSettle={onSettle}
                            onViewOwnerInventoryClick={onViewOwnerInventoryClick}
                            showFooter={false}
                            savedListingIds={savedListingIds}
                            lazyLoad={lazyLoad}
                            loading={lazyLoad ? 'lazy' : 'eager'}
                            carouselSettings={carouselSettings}
                            showCompareButton={showCompareButton}
                            onCompareClick={handleCompareListingsClick}
                            isCompared={isCompared}
                        />
                    </Panel.Footer>
                )}
        </Panel>
    );
}

InventoryAlphaListing.propTypes = {
    /**
     * The addOns props to enable InventoryListingAddOns
     **/
    addOns: PropTypes.shape({
        label: PropTypes.string,
        selected: PropTypes.bool,
        onListingRemove: PropTypes.func,
        onListingSelect: PropTypes.func,
        shareButton: PropTypes.element,
    }),
    /**
     * Settings for carousel behavior
     */
    carouselSettings: PropTypes.object,
    /**
     * The emailOwnerProps Object has the props to pass through to the EmailCTA component.
     */
    emailOwnerProps: PropTypes.object,
    /**
     * Whether to enable My Wallet functionality
     */
    enableMyWallet: PropTypes.bool,
    /**
     * Whether this is an active deal
     */
    isActiveDeal: PropTypes.bool,
    /**
     * Whether this is an alpha market
     */
    isAlphaMarket: PropTypes.bool,
    /**
     * If true, the component will render in Added mode.
     */
    isCompared: PropTypes.bool,
    /**
     * Whether the alpha listing matches the exact criteria
     */
    isExactMatch: PropTypes.bool,
    /**
     * If true, the component will render in saved mode.
     */
    isSaved: PropTypes.bool,
    /**
     * If true, the image will load when in the viewport using LazyRender
     */
    lazyLoad: PropTypes.bool,
    /**
     * The listing object has all of the data specific to the listing/seller.
     */
    listing: PropTypes.shape({
        specifications: PropTypes.object,
        owner: PropTypes.object,
        pricingDetail: PropTypes.object,
        payments: PropTypes.object,
        accelerate: PropTypes.bool,
    }),
    /**
     * The listingCTA object has attributes passed to the anchor.
     */
    listingCTA: PropTypes.object,
    /**
     * Array of listings for carousel
     */
    listings: PropTypes.array,
    /**
     * Total count of listings
     */
    listingsCount: PropTypes.number,
    /**
     * Use to control the loading attribute on the listing image when lazyLoad is false
     */
    loading: PropTypes.string,
    /**
     * Callback for active deal details click
     */
    onActiveDealDetailsClick: PropTypes.func,
    /**
     * Callback for alpha listing click
     */
    onAlphaListingClick: PropTypes.func,
    /**
     * Callback for alpha wallet click
     */
    onAlphaWalletClick: PropTypes.func,
    /**
     * Callback for check status click
     */
    onCheckStatusClick: PropTypes.func,
    /**
     * Callback for collapsible header click
     */
    onCollapsibleHeaderClick: PropTypes.func,
    /**
     * Callback for compare click
     */
    onCompareClick: PropTypes.func,
    /**
     * Callback for context menu
     */
    onContextMenu: PropTypes.func,
    /**
     * Callback for inventory listing click
     */
    onInventoryListingClick: PropTypes.func,
    /**
     * Callback for payment CTA click
     */
    onPaymentCTAClick: PropTypes.func,
    /**
     * The callback function triggered by Pricing click
     */
    onPricingDetailClick: PropTypes.func,
    /**
     * Callback for Save action.
     */
    onSaveToggle: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),
    /**
     * Callback for settle action
     */
    onSettle: PropTypes.func,
    /**
     * Callback for tile click
     */
    onTileClick: PropTypes.func,
    /**
     * Callback for view owner inventory click
     */
    onViewOwnerInventoryClick: PropTypes.func,
    /**
     * Device orientation
     */
    orientation: PropTypes.oneOf(['portrait', 'landscape']),
    /**
     * Owner object
     */
    owner: PropTypes.object,
    /**
     * analytics parameter
     */
    parentId: PropTypes.string,
    /**
     * personal payment props
     */
    personalPaymentProps: PropTypes.object,
    /**
     * The callback function fires when owner phone is called
     */
    phoneClickHandler: PropTypes.func,
    /**
     * Array of saved listing IDs
     */
    savedListingIds: PropTypes.array,
    /**
     * Share button component
     */
    shareButton: PropTypes.element,
    /**
     * Whether to show alpha payment ribbon
     */
    showAlphaPaymentRibbon: PropTypes.bool,
    /**
     * Whether to show compare button
     */
    showCompareButton: PropTypes.bool,
    /**
     * Function to update listing payments
     */
    updateListingPayments: PropTypes.func,
};

InventoryAlphaListing.displayName = 'InventoryAlphaListing';

export default React.memo(InventoryAlphaListing, _isEqual);
